import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';

/** 그리드와 동일한 엑셀 컬럼 순서 (서명은 이미지로 별도 삽입) */
const EXCEL_COLUMNS = [
  { field: 'no', header: 'NO', width: 6 },
  { field: 'userDepNm', header: '부서', width: 16 },
  { field: 'userRank', header: '직위', width: 10 },
  { field: 'userNm', header: '성명', width: 12 },
  { field: 'issuSignPath', header: '서명', width: 20, imageColumn: true },
  { field: 'equipNm', header: '보호구명', width: 20 },
  { field: 'createDate', header: '지급일자', width: 18, dateTime: true },
  { field: 'qty', header: '수량', width: 8, number: true },
  { field: 'issuUserNm', header: '지급인', width: 10 },
  { field: 'remark', header: '비고', width: 28 },
];

const BuildIssuSignImageUrl = (raw) => {
  if (raw == null || String(raw).trim() === '') return '';
  const normalized = String(raw).replace(/\\/g, '/');
  const enc = Commons.EncodeUrl(normalized);
  const base = String(Global.file?.useUrl ?? '').replace(/\/?$/, '/');
  return enc ? `${base}files/${enc}` : '';
};

const ArrayBufferToBase64 = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + chunk, bytes.length))
    );
  }
  return btoa(binary);
};

const ResolveImageExtension = (pathStr, contentType) => {
  const ct = (contentType || '').toLowerCase();
  if (ct.includes('jpeg') || ct.includes('jpg')) return 'jpeg';
  if (ct.includes('png')) return 'png';
  if (ct.includes('gif')) return 'gif';
  const low = String(pathStr || '').toLowerCase();
  if (low.endsWith('.jpg') || low.endsWith('.jpeg')) return 'jpeg';
  if (low.endsWith('.gif')) return 'gif';
  if (low.endsWith('.png')) return 'png';
  return 'png';
};

const FetchSignImageForExcel = async (issuSignPath) => {
  const url = BuildIssuSignImageUrl(issuSignPath);
  if (!url) return null;
  // 리포트 출력(PPE_BASIC_03F)과 동일: 캐시 무효화로 서명 이미지 최신 바이너리 확보
  const fetchUrl = `${url}?t=${new Date().getTime()}`;
  try {
    const response = await fetch(fetchUrl);
    if (!response.ok) return null;
    const arrayBuffer = await response.arrayBuffer();
    if (!arrayBuffer || arrayBuffer.byteLength === 0) return null;
    const extension = ResolveImageExtension(
      issuSignPath,
      response.headers.get('content-type')
    );
    return {
      base64: ArrayBufferToBase64(arrayBuffer),
      extension,
    };
  } catch {
    return null;
  }
};

const FormatCellValue = (row, col) => {
  const v = row[col.field];
  if (col.dateTime) {
    if (!v) return '';
    return dayjs(v).format('YYYY-MM-DD HH:mm');
  }
  if (col.number) {
    if (v == null || v === '') return '';
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
  }
  if (col.imageColumn) return '';
  if (v == null) return '';
  return String(v);
};

/**
 * 보호구 지급대장 엑셀 생성 (서명 컬럼은 issuSignPath 기준 이미지 삽입)
 * @param {Array<object>} rows 그리드 rowData
 * @returns {Promise<ArrayBuffer>}
 */
export const BuildPpeBasic03LedgerExcelBuffer = async (rows) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('보호구지급대장', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  EXCEL_COLUMNS.forEach((col, i) => {
    sheet.getColumn(i + 1).width = col.width;
  });

  const headerRow = sheet.getRow(1);
  EXCEL_COLUMNS.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = col.header;
    cell.font = { bold: true };
  });
  headerRow.commit();

  const signColIndex0 = EXCEL_COLUMNS.findIndex((c) => c.imageColumn);
  const FIRST_DATA_ROW = 2;
  const IMAGE_EXT_WIDTH = 120;
  const IMAGE_EXT_HEIGHT = 72;

  const dataRows = Array.isArray(rows) ? rows : [];

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const excelRow = sheet.getRow(FIRST_DATA_ROW + i);
    excelRow.height = 86;

    EXCEL_COLUMNS.forEach((col, j) => {
      const cell = excelRow.getCell(j + 1);
      cell.value = FormatCellValue(row, col);
    });
    excelRow.commit();
  }

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const pathVal = row?.issuSignPath;
    if (!pathVal || !String(pathVal).trim()) continue;

    const img = await FetchSignImageForExcel(pathVal);
    if (!img) continue;

    const imageId = workbook.addImage({
      base64: img.base64,
      extension: img.extension,
    });

    const tlRow0 = FIRST_DATA_ROW - 1 + i;
    sheet.addImage(imageId, {
      tl: { col: signColIndex0, row: tlRow0 },
      ext: { width: IMAGE_EXT_WIDTH, height: IMAGE_EXT_HEIGHT },
    });
  }

  return workbook.xlsx.writeBuffer();
};
